import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, LogIn, LogOut, Plus, Receipt, CalendarDays } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EMPTY_FUEL, FuelClaim, toFuelPayload } from '../components/FuelClaim';
import { MyPerformance } from '../components/MyPerformance';
import { MyProfile } from '../components/MyProfile';
import { fuelProblems } from '@jt/domain';
import { SlaBadge, slaEdgeClass } from '../components/SlaBadge';
import { PriorityBadge, StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * The technician's own screen.
 *
 * One page rather than a section of the console, because it is read on a phone
 * with one hand, standing up, and usually before eight in the morning. The
 * order is the order of the day: am I checked in, what am I doing, what do I
 * need to claim, when am I off.
 *
 * A technician who only ever taps the link in their email never sees this. It
 * exists for the things that belong to a person rather than to one job.
 */
export function MyWorkPage() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'jobs' | 'performance' | 'expenses' | 'leave' | 'profile'>(
    'jobs',
  );

  const { data: today } = useQuery({ queryKey: ['attendance-today'], queryFn: api.attendanceToday });
  /*
   * `mine`, not the plain list.
   *
   * This panel is headed "Assigned to you" and it was calling the general
   * ticket list. For a technician the server narrows that to their own work
   * anyway, so it looked correct — but an administrator is allowed to see
   * everything, and so their own page listed every ticket in the business,
   * including ones assigned to nobody at all.
   *
   * The heading was right and the query was wrong.
   */
  const { data: jobs, isPending } = useQuery({
    queryKey: ['my-jobs'],
    queryFn: () => api.tickets({ mine: 'true' }),
  });

  /**
   * Position, if the browser will give it and quickly.
   *
   * Never blocking: a technician standing in a building with no GPS lock must
   * still be able to start their day. A refused or slow fix records the time
   * without the place, which is the right trade — the time is what payroll
   * needs, the place is corroboration.
   */
  const position = (): Promise<{ lat?: number; lng?: number }> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      const timer = setTimeout(() => resolve({}), 6000);
      navigator.geolocation.getCurrentPosition(
        (p) => {
          clearTimeout(timer);
          resolve({ lat: p.coords.latitude, lng: p.coords.longitude });
        },
        () => {
          clearTimeout(timer);
          resolve({});
        },
        { enableHighAccuracy: false, timeout: 6000 },
      );
    });

  const clock = useMutation({
    mutationFn: async (direction: 'in' | 'out') => {
      const where = await position();
      return direction === 'in' ? api.checkIn(where) : api.checkOut(where);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance-today'] }),
  });

  const checkedIn = today?.checkedIn ?? false;

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-16">
      <div>
        <h2 className="font-heading text-2xl">Good day, {user?.name.split(' ')[0]}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {new Date().toLocaleDateString('en-KE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </p>
      </div>

      {/* ── Am I working ─────────────────────────────────────────────────── */}
      <section className={`card ${checkedIn ? 'border-secondary-300 bg-secondary-50/40' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-medium">
              <Clock className="h-4 w-4" aria-hidden="true" />
              {checkedIn ? 'You are checked in' : 'Not checked in'}
            </div>
            {today?.record?.checkInAt && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                Started at{' '}
                {new Date(today.record.checkInAt).toLocaleTimeString('en-KE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {today.record.checkOutAt &&
                  ` · finished at ${new Date(today.record.checkOutAt).toLocaleTimeString('en-KE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`}
              </p>
            )}
          </div>

          <button
            type="button"
            className={checkedIn ? 'btn-secondary' : 'btn-primary'}
            disabled={clock.isPending}
            onClick={() => clock.mutate(checkedIn ? 'out' : 'in')}
          >
            {clock.isPending ? (
              'Saving…'
            ) : checkedIn ? (
              <>
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Check out
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4" aria-hidden="true" />
                Check in
              </>
            )}
          </button>
        </div>
        {clock.isError && (
          <p className="mt-2 text-sm text-danger-700">{(clock.error as Error).message}</p>
        )}
      </section>

      <div className="flex gap-1">
        {(
          [
            ['jobs', 'My jobs'],
            ['performance', 'My performance'],
            ['expenses', 'Expenses'],
            ['leave', 'Leave'],
            ['profile', 'Profile'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
              tab === id
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <MyProfile />}
      {tab === 'performance' && <MyPerformance />}

      {tab === 'jobs' && (
        <section className="card">
          <h3 className="mb-3 font-heading text-lg">Assigned to you</h3>
          {isPending && <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-500" />}
          {jobs && jobs.tickets.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing assigned to you right now.
            </p>
          )}
          <ul className="divide-y divide-border">
            {(jobs?.tickets ?? []).map((job) => (
              <li key={job.id} className={`py-3 pl-2 ${slaEdgeClass(job.slaState)}`}>
                <Link to={`/tickets/${job.id}`} className="block hover:opacity-80">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-primary-700">{job.reference}</span>
                    <StatusBadge status={job.status} />
                    <PriorityBadge priority={job.priority} />
                    <SlaBadge state={job.slaState} percent={job.slaPercent} />
                  </div>
                  <p className="mt-0.5 text-sm">{job.title}</p>
                  {job.areaName && (
                    <p className="text-xs text-muted-foreground">{job.areaName}</p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'expenses' && <MyExpenses />}
      {tab === 'leave' && <MyLeave />}
    </div>
  );
}

// ─── Expenses ───────────────────────────────────────────────────────────────

const CATEGORIES = ['fuel', 'fare', 'airtime', 'materials', 'tools', 'accommodation', 'other'];

function MyExpenses() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    category: 'fuel',
    amountKes: '',
    spentOn: new Date().toISOString().slice(0, 10),
    description: '',
    receipt: null as { data: string; mimeType: string } | null,
  });
  const [fuel, setFuel] = useState(EMPTY_FUEL);

  const { data } = useQuery({ queryKey: ['my-expenses'], queryFn: () => api.expenses() });

  /**
   * A fuel claim is described, not priced. The API computes the amount from the
   * tank and ignores whatever is in the amount box, so sending it would be
   * sending a number that loses.
   */
  const isFuel = form.category === 'fuel';
  const fuelPayload = isFuel ? toFuelPayload(fuel) : null;
  const fuelReady = Boolean(fuelPayload) && fuelProblems(fuelPayload!).length === 0;

  const claim = useMutation({
    mutationFn: () =>
      api.createExpense({
        category: form.category,
        amountKes: isFuel ? 0 : Number(form.amountKes),
        spentOn: form.spentOn,
        description: form.description || undefined,
        receipt: form.receipt ?? undefined,
        ...(isFuel && fuelPayload ? { fuel: fuelPayload } : {}),
      }),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      setForm({ ...form, amountKes: '', description: '', receipt: null });
      setFuel(EMPTY_FUEL);
      await queryClient.invalidateQueries({ queryKey: ['my-expenses'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-lg">Your claims</h3>
        {!open && (
          <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Claim
          </button>
        )}
      </div>

      {open && (
        <form
          className="mb-4 space-y-3 rounded-xl border border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            claim.mutate();
          }}
        >
          {error && <p className="rounded-lg bg-danger-50 p-2 text-sm text-danger-700">{error}</p>}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">What for</span>
              <select
                className="input w-full"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>

            {/* Hidden for fuel: the amount comes from the tank below, and two
                boxes that disagree is worse than one that is derived. */}
            {!isFuel && (
              <label className="block text-sm">
                <span className="mb-1 block font-medium">How much (KES)</span>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  className="input w-full"
                  value={form.amountKes}
                  onChange={(e) => setForm({ ...form, amountKes: e.target.value })}
                  required
                />
              </label>
            )}

            <label className="block text-sm">
              <span className="mb-1 block font-medium">When</span>
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                className="input w-full"
                value={form.spentOn}
                onChange={(e) => setForm({ ...form, spentOn: e.target.value })}
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Receipt</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="w-full text-xs"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const data = await file.arrayBuffer();
                  setForm({
                    ...form,
                    receipt: {
                      data: btoa(String.fromCharCode(...new Uint8Array(data))),
                      mimeType: file.type,
                    },
                  });
                }}
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Needed above KES 500.
              </span>
            </label>
          </div>

          {isFuel && <FuelClaim value={fuel} onChange={setFuel} />}

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Note (optional)</span>
            <input
              className="input w-full"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Kisii to Suneka and back"
            />
          </label>

          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={claim.isPending || (isFuel && !fuelReady)}
            >
              {claim.isPending ? 'Sending…' : 'Send for approval'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {data && (
        <>
          <div className="mb-3 flex flex-wrap gap-4 text-sm">
            <span>
              <span className="text-muted-foreground">Waiting: </span>
              <span className="font-medium tabular-nums">KES {data.totals.pending}</span>
            </span>
            <span>
              <span className="text-muted-foreground">Approved: </span>
              <span className="font-medium tabular-nums text-secondary-700">
                KES {data.totals.approved}
              </span>
            </span>
          </div>

          {data.expenses.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nothing claimed yet.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {data.expenses.map((e) => (
                <li key={e.id} className="flex items-start justify-between gap-3 py-2">
                  <div>
                    <span className="font-medium">{e.category}</span>
                    <span className="ml-2 tabular-nums">KES {e.amountKes}</span>
                    <div className="text-xs text-muted-foreground">
                      {new Date(e.spentOn).toLocaleDateString('en-KE')}
                      {e.description && ` · ${e.description}`}
                    </div>
                    {e.status === 'rejected' && e.decisionNote && (
                      <div className="mt-0.5 text-xs text-danger-700">{e.decisionNote}</div>
                    )}
                  </div>
                  <StatusPill status={e.status} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// ─── Leave ──────────────────────────────────────────────────────────────────

function MyLeave() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    type: 'annual',
    startDate: today,
    endDate: today,
    reason: '',
  });

  const { data } = useQuery({ queryKey: ['my-leave'], queryFn: () => api.leave() });
  const balance = useQuery({ queryKey: ['my-leave-balance'], queryFn: api.myLeaveBalance });

  const request = useMutation({
    mutationFn: () => api.requestLeave(form),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['my-leave'] });
      // The balance moved: a pending request is held against it.
      await queryClient.invalidateQueries({ queryKey: ['my-leave-balance'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-lg">Your leave</h3>
        {!open && (
          <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            Request
          </button>
        )}
      </div>

      {/*
        The balance, before the form.

        It is the number that decides whether a request is worth making, so it
        belongs above the button rather than inside an error message after the
        request has been refused.
      */}
      {balance.data?.balance && (
        <div className="mb-4 rounded-xl border border-border bg-muted/30 p-3">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span>
              <span className="block text-xs text-muted-foreground">Days you can take</span>
              <span className="text-2xl font-semibold tabular-nums">
                {balance.data.balance.remaining}
              </span>
            </span>
            <span className="text-sm text-muted-foreground">
              <span className="tabular-nums">{balance.data.balance.accrued}</span> earned ·{' '}
              <span className="tabular-nums">{balance.data.balance.taken}</span> taken
              {balance.data.balance.pending > 0 && (
                <>
                  {' '}
                  · <span className="tabular-nums">{balance.data.balance.pending}</span> waiting for
                  a decision
                </>
              )}
            </span>
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            {balance.data.balance.annualEntitlement} working days a year, earned a twelfth each
            month. Ask at least 5 working days ahead — sickness is the exception.
          </p>

          {/*
            Said plainly rather than hidden. The balance is only as good as the
            start date it counts from, and a guessed one produces an
            authoritative-looking number nobody has checked.
          */}
          {balance.data.balance.employmentDateAssumed && (
            <p className="mt-1 text-xs text-accent-700">
              Counted from when your record was created, because no employment start date is set.
              Ask HR to set it and this will correct itself.
            </p>
          )}
        </div>
      )}

      {open && (
        <form
          className="mb-4 space-y-3 rounded-xl border border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            request.mutate();
          }}
        >
          {error && <p className="rounded-lg bg-danger-50 p-2 text-sm text-danger-700">{error}</p>}

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Kind</span>
              <select
                className="input w-full"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {['annual', 'sick', 'unpaid', 'compassionate'].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">From</span>
              <input
                type="date"
                className="input w-full"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">To</span>
              <input
                type="date"
                min={form.startDate}
                className="input w-full"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Reason (optional)</span>
            <input
              className="input w-full"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={request.isPending}>
              {request.isPending ? 'Sending…' : 'Send request'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {data && data.requests.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No requests yet.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {(data?.requests ?? []).map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-3 py-2">
              <div>
                <span className="font-medium">{r.type}</span>
                <span className="ml-2 text-muted-foreground">
                  {new Date(r.startDate).toLocaleDateString('en-KE')} –{' '}
                  {new Date(r.endDate).toLocaleDateString('en-KE')} · {r.days} day{r.days === 1 ? '' : 's'} away
                  {/* And what it costs, which is the number that comes off a balance.
                      Friday to Monday is four days away and two days of leave. */}
                  {r.workingDays !== undefined && r.workingDays !== r.days && (
                    <> · {r.workingDays} working day{r.workingDays === 1 ? '' : 's'}</>
                  )}
                </span>
                {r.status === 'rejected' && r.decisionNote && (
                  <div className="mt-0.5 text-xs text-danger-700">{r.decisionNote}</div>
                )}
              </div>
              <StatusPill status={r.status} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Word first, colour second — the two refusal states must not rely on hue. */
function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'approved'
      ? 'bg-secondary-50 text-secondary-700'
      : status === 'rejected'
        ? 'bg-danger-50 text-danger-700'
        : 'bg-accent-50 text-accent-700';
  return <span className={`badge shrink-0 ${tone}`}>{status}</span>;
}

export { Receipt };
